/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import isNil from 'lodash/isNil';
import { defineComponent } from 'vue';

// @ts-ignore Není to ESM.
import template from './status-info-dialog-basic.html';

import { $root } from '../../player/player';
import { toLocalDate } from '../iso-string';

/**
 * @import { PropType } from 'vue'
 * @import { StatusInfoBasic, StorageUsed } from '.'
 * @import { ISOString } from '../iso-string'
 * @import { TimeZone } from '../time'
 */

export default defineComponent({
  name: 'StatusInfoDialogBasic',
  template,
  computed: {
    formattedInfo() {
      return {
        application: formatAndAddFallbacks({
          ...this.info?.application,
          ...(this.info?.application.server && {
            server: formatUrl(this.info?.application.server)
          }),
          id: formatAppId(this.info?.application.id)
        }),

        network: formatAndAddFallbacks(this.info?.network || {}),
        system: formatAndAddFallbacks({
          ...this.info?.system,
          storage: {
            used: formatStorageUsed(this.info?.system.storage.used)
          }
        }),
        time: formatAndAddFallbacks({
          ...this.info?.time,
          local: toLocalDateSafe(this.info?.time.local, this.info?.time.zone),
          boot: toLocalDateSafe(this.info?.time.boot, this.info?.time.zone),
          contentTimestamp: toLocalDateSafe(
            this.info?.time.contentTimestamp,
            this.info?.time.zone
          ),
          ...(!isNil(this.info?.time.localTimeServerOffset) && {
            localTimeServerOffsetSec: formatServerOffset(
              this.info?.time.localTimeServerOffset
            )
          })
        })
      };
    },
    /**
     * @returns {{enabled: boolean | undefined, enabledValue: string, server: string[]}}
     */
    ntpInfo() {
      const ntpInfo = this.info?.time.ntpInfo;
      if (isNil(ntpInfo)) {
        return { enabled: undefined, enabledValue: '-', server: [] };
      }
      return {
        enabled: ntpInfo.enabled,
        enabledValue: ntpInfo.enabled ? 'Yes' : 'No',
        server: ntpInfo.server?.map(({ host }) => host) ?? []
      };
    },
    /**
     * @returns {{enabled: boolean | undefined, enabledValue: string, address: string | undefined}}
     */
    proxyServerInfo() {
      const proxyInfo = this.info?.network.proxyServerInfo;
      if (isNil(proxyInfo)) {
        return { address: undefined, enabled: undefined, enabledValue: '-' };
      }
      return {
        address: proxyInfo.host
          ? `${proxyInfo.host}${proxyInfo.port ? `:${proxyInfo.port}` : ''}`
          : undefined,
        enabled: proxyInfo.enabled,
        enabledValue: proxyInfo.enabled ? 'Yes' : 'No'
      };
    }
  },
  data() {
    const { networkListener } = $root(this);
    return {
      isOnline: networkListener.isOnline()
    };
  },
  created() {
    this.onOnlineChanged = this.onOnlineChanged.bind(this);
  },
  mounted() {
    const { networkListener } = $root(this);
    // Sledování stavu připojení k síti.
    networkListener.on('onlineChanged', this.onOnlineChanged);
  },
  destroyed() {
    const { networkListener } = $root(this);
    networkListener.off('onlineChanged', this.onOnlineChanged);
  },
  methods: {
    /**
     * @param {boolean} online
     */
    onOnlineChanged(online) {
      this.isOnline = online;
    }
  },
  emits: {},
  props: {
    // Nesmie byť undefined.
    info: {
      /** @type {PropType<StatusInfoBasic>} */
      type: Object,
      require: true
    }
  }
});

/**
 * Formátuje hodnoty objektu: ořezává mezery u stringů a přidává
 * fallback pro undefined nebo prázdné hodnoty. Nezanoruje se.
 * @template T
 * @param {Record<string, T | undefined>} obj
 * @returns {Record<string, T | '-'>}
 */
function formatAndAddFallbacks(obj) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, formatValue(value)])
  );
}

/**
 * Stručí ID aplikace na 8 znaků a přidá '...' pokud je ID delší než 8 znaků.
 * @param {string | undefined} id
 * @returns {string | undefined}
 */
function formatAppId(id) {
  if (id) {
    const idTruncated = id.split('-')[0].slice(0, 8);
    if (idTruncated.length < id.length) {
      return `${idTruncated}...`;
    }
    return idTruncated;
  }
  return id;
}

/**
 * Převede časový posun serveru.
 * @param {number} offset [ms]
 * @returns {string} zaokrouhlený na 3 desetinná místa [s]
 */
function formatServerOffset(offset) {
  return (offset / 1000).toFixed(3);
}

/**
 * Formátuje využití úložiště na pole dvojic [název, využití v procentech].
 * @param {StorageUsed | undefined} storageUsed
 * @returns {[string, string][]}
 */
function formatStorageUsed(storageUsed) {
  if (!storageUsed) {
    return [];
  }
  return storageUsed.map(([id, used]) => [id, (used * 100).toFixed(1)]);
}

// Prevzaté z https://css-tricks.com/better-line-breaks-for-long-urls/
/**
 * Vloží <wbr> do URL adresy na vhodná místa pro zalomení řádku podľa
 * The Chicago Manual of Style.
 * @param {string} url
 * @returns {string}
 */
function formatUrl(url) {
  const doubleSlash = url.split('//');

  // Format the strings on either side of double slashes separately
  const formatted = doubleSlash
    .map(
      (str) =>
        // Insert a word break opportunity after a colon
        str
          .replace(/(?<after>:)/giu, '$1<wbr>')
          // Before a single slash, tilde, period, comma, hyphen,
          // underline, question mark, number sign, or percent symbol
          .replace(/(?<before>[/~.,\-_?#%])/giu, '<wbr>$1')
          // Before and after an equals sign or ampersand
          .replace(/(?<beforeAndAfter>[=&])/giu, '<wbr>$1<wbr>')
      // Reconnect the strings with word break opportunities after
      // double slashes
    )
    .join('//<wbr>');

  return formatted;
}

/**
 * Formátuje jednotlivou hodnotu: ořezává mezery u stringů a přidává fallback.
 * @template T
 * @param {T | undefined} value
 * @returns {T | '-'}
 */
function formatValue(value) {
  let v = value;
  if (typeof v === 'string') {
    v = /** @type {T} */ (
      // eslint-disable-next-line no-control-regex
      v.trim().replace(/[\u0000-\u001F\u007F-\u009F\u200B]/g, '')
    );
  }
  if (v === undefined || v === '') {
    return '-';
  }
  return v;
}

/**
 * Bezpečně převede ISOString na lokální datum.
 * @param {ISOString | undefined} isoDate
 * @param {TimeZone | undefined} timeZone
 * @returns {string | undefined}
 */
function toLocalDateSafe(isoDate, timeZone) {
  if (isoDate) {
    return toLocalDate(isoDate, timeZone);
  }
  return isoDate;
}
